import FileBrowserWrapper from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/file-browser-wrapper.tsx";
import {
  type BucketObject,
  getParentDirectory,
} from "@via/pages/data/data-sources/utils/helpers.tsx";
import {
  type DataConnectionCredentials,
  DataSourceProvider,
  parseRemoteStorageUri,
  type RemoteStorageFileListResponse,
} from "common";
import { type JSX, useEffect, useMemo, useState } from "react";
import {
  remoteStorageErrorMessage,
  remoteStorageRefusalReason,
  useRemoteStorageFolderList,
} from "./remote-storage.api.tsx";

interface Props {
  credentialId: number;
  startPath: string;
  onContinue: (files: unknown) => void;
  cart?: BucketObject[];
  setCart?: (cart: BucketObject[]) => void;
  onHostKeyChanged: () => void;
}

/** The shared folder browser over one remote server. Paths are relative to the server's login folder. */
export default function RemoteStorageFolderBrowser({
  credentialId,
  startPath,
  onContinue,
  cart,
  setCart,
  onHostKeyChanged,
}: Props): JSX.Element {
  const [prefix, setPrefix] = useState(startPath);
  const [folderList, setFolderList] = useState<RemoteStorageFileListResponse>();
  const [error, setError] = useState<string | null>(null);
  const list = useRemoteStorageFolderList();

  useEffect(() => {
    // Only the newest request may change the screen: a slow answer for a folder the user already
    // left, or one that lands after unmount, is dropped.
    let current = true;
    setError(null);
    list
      .mutateAsync({ credentialId, path: prefix })
      .then(listing => {
        if (current) setFolderList(listing);
      })
      .catch(failure => {
        if (!current) return;
        // Fail closed: a failed listing never leaves an older listing on screen.
        setFolderList(undefined);
        setError(remoteStorageErrorMessage(failure));
        if ("host_key_changed" === remoteStorageRefusalReason(failure)) {
          onHostKeyChanged();
        }
      });
    return () => {
      current = false;
    };
  }, [credentialId, prefix]);

  const connection = useMemo(
    () =>
      ({
        bucketName: String(credentialId),
        prefix,
        savedCredentialId: credentialId,
      }) as unknown as DataConnectionCredentials,
    [credentialId, prefix]
  );

  return (
    <div>
      {error && (
        <p className="my-2 text-sm text-rose-600" role="alert">
          {error}
        </p>
      )}
      {folderList?.truncated && (
        <p className="my-2 text-sm text-amber-700">
          This folder holds more entries than can be shown. Open a folder below
          it.
        </p>
      )}
      <FileBrowserWrapper
        connectionCredentials={connection}
        folderList={folderList}
        dataSourceProvider={DataSourceProvider.RemoteStorage}
        onObjectClick={(object: BucketObject) =>
          setPrefix(
            ".." === object.id
              ? (getParentDirectory(prefix) ?? "")
              : object.name
          )
        }
        onPathChange={({ path }: { path: string }) => {
          const location = parseRemoteStorageUri(path);
          if (location && location.credentialId === credentialId) {
            setPrefix(location.path);
          } else {
            setError("Enter a remote:// location on this server.");
          }
        }}
        getExtraFolderObjects={async () => undefined}
        onContinue={onContinue}
        isGetFolderListLoading={list.isPending}
        isGetExtraFolderListLoading={false}
        cart={cart}
        setCart={setCart}
      />
    </div>
  );
}
